import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconSchool } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";
import VideoRecommendationsPanel from "../shared/VideoRecommendationsPanel/VideoRecommendationsPanel";

function TrainingTabHomePage() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [courses, setCourses] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [programs, setPrograms] = useState([]);

  const [featuredVideoUrl, setFeaturedVideoUrl] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [courseVideos, setCourseVideos] = useState([]);
  const [activeInfoTab, setActiveInfoTab] = useState("description");
  const [autoPlayVideo, setAutoPlayVideo] = useState(false);

  const [programsFilterTab, setProgramsFilterTab] = useState("To Do");

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [playbackSeconds, setPlaybackSeconds] = useState(0);

  const videoRef = useRef(null);
  const courseVideoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const trackingIntervalRef = useRef(null);
  const lastTimeUpdateRef = useRef(0);
  const currentVideoTimeRef = useRef(0);
  const playbackSecondsRef = useRef(0);

  useEffect(() => {
    currentVideoTimeRef.current = currentVideoTime;
  }, [currentVideoTime]);

  useEffect(() => {
    playbackSecondsRef.current = playbackSeconds;
  }, [playbackSeconds]);

  const sendVideoLogUpdate = React.useCallback(
    async (secondsWatched, currentPlaybackSeconds) => {
      if (
        !selectedVideo ||
        secondsWatched === 0 ||
        currentPlaybackSeconds === 0
      ) {
        return;
      }

      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        setPlaybackSeconds(0);
        playbackSecondsRef.current = 0;

        const res = await fetch(
          getApiEndpoint("/v1/training/videolog/update"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              video_id: selectedVideo.id,
              seconds_watched: secondsWatched,
              playback_seconds: currentPlaybackSeconds,
            }),
          }
        );

        if (res.ok && secondsWatched + 1 >= selectedVideo.duration) {
          if (selectedCourse && selectedCourse.id) {
            const response = await fetch(
              getApiEndpoint("/v1/training/view-course"),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${jwt}`,
                },
                body: JSON.stringify({ course_id: selectedCourse.id }),
              }
            );

            if (response.ok) {
              const response_body = await response.json();
              const data = response_body.data;
              if (data.videos && Array.isArray(data.videos)) {
                setCourseVideos(data.videos);

                const activeVideoData = data.videos.find(
                  video => !video.video_completed
                );

                if (activeVideoData) {
                  setSelectedVideo(activeVideoData);
                } else if (data.videos.length > 0) {
                  setSelectedVideo(data.videos[0]);
                }
              }
            }
          }
        }
      } catch (err) {}
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedVideo, selectedCourse, getVerifiedJWT]
  );

  const handleCourseVideoTimeUpdate = React.useCallback(currentTime => {
    if (!Number.isFinite(currentTime)) return;

    setCurrentVideoTime(currentTime);
    currentVideoTimeRef.current = currentTime;

    if (lastTimeUpdateRef.current > 0) {
      const timeElapsed = Math.abs(currentTime - lastTimeUpdateRef.current);
      if (timeElapsed > 0) {
        setPlaybackSeconds(prev => {
          const next = prev + timeElapsed;
          playbackSecondsRef.current = next;
          return next;
        });
      }
    }
    lastTimeUpdateRef.current = currentTime;
  }, []);

  const flushVideoLogUpdate = React.useCallback(() => {
    const t = currentVideoTimeRef.current;
    const p = playbackSecondsRef.current;
    if (t > 0) {
      sendVideoLogUpdate(Math.ceil(t), Math.floor(p));
    }
  }, [sendVideoLogUpdate]);

  useEffect(() => {
    if (!isVideoPlaying) {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
      return;
    }

    if (trackingIntervalRef.current) return;

    trackingIntervalRef.current = setInterval(() => {
      const t = currentVideoTimeRef.current;
      const p = playbackSecondsRef.current;

      if (t > 0) {
        sendVideoLogUpdate(Math.ceil(t), Math.floor(p));
      }
    }, 10000);

    return () => {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
    };
  }, [isVideoPlaying, sendVideoLogUpdate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const jwt = await getVerifiedJWT();
        if (!jwt) {
          setCourses([]);
          setAllVideos([]);
          setPrograms([]);
          return;
        }

        const [dashboardRes, programsRes] = await Promise.all([
          fetch(getApiEndpoint("/v1/training/dashboard-data"), {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            cache: "no-cache",
          }),
          fetch(getApiEndpoint("/v1/programs/mine"), {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            cache: "no-cache",
          }),
        ]);

        if (!dashboardRes.ok) {
          throw new Error("Failed to fetch training dashboard data");
        }

        const dashboardResult = await dashboardRes.json();
        const dashboardData = dashboardResult?.data || {};

        setCourses(dashboardData.courses || []);
        setAllVideos(dashboardData.videos || []);

        if (dashboardData.featuredVideo?.video_url) {
          setFeaturedVideoUrl(dashboardData.featuredVideo.video_url);
        }

        if (programsRes.ok) {
          const programsResult = await programsRes.json();
          setPrograms(programsResult?.data || []);
        } else {
          setPrograms([]);
        }
      } catch (err) {
        setError(err.message || "Failed to load training home data");
        setCourses([]);
        setAllVideos([]);
        setPrograms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getVerifiedJWT]);

  useEffect(() => {
    if (shouldLoadVideo && featuredVideoUrl) {
      const timer = setTimeout(() => setShowVideo(true), 300);
      return () => clearTimeout(timer);
    }

    setShowVideo(false);
    return undefined;
  }, [featuredVideoUrl, shouldLoadVideo]);

  useEffect(() => {
    if (showVideo && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [showVideo]);

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setVideoReady(false);
    setCurrentVideoTime(0);
    setDuration(0);
    setIsVideoPlaying(false);
    setPlaybackSeconds(0);
    setActiveInfoTab("description");

    currentVideoTimeRef.current = 0;
    playbackSecondsRef.current = 0;
    lastTimeUpdateRef.current = 0;

    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
      trackingIntervalRef.current = null;
    }

    if (courseVideoRef.current) {
      courseVideoRef.current.pause();
    }
  }, [selectedVideo]);

  const getResumeTimeForVideo = React.useCallback((video, videoDuration) => {
    if (!video || video.video_completed) return 0;

    const rawResumeTime = Number(
      video.playback_seconds ?? video.seconds_watched ?? 0
    );

    if (!Number.isFinite(rawResumeTime) || rawResumeTime <= 0) return 0;

    if (Number.isFinite(videoDuration) && videoDuration > 0) {
      return Math.min(
        Math.floor(rawResumeTime),
        Math.max(videoDuration - 3, 0)
      );
    }

    return Math.floor(rawResumeTime);
  }, []);

  const handleCourseVideoLoadedMetadata = () => {
    if (!courseVideoRef.current) return;

    const videoElement = courseVideoRef.current;
    const loadedDuration = Number(videoElement.duration) || 0;
    setDuration(loadedDuration);

    const resumeTime = getResumeTimeForVideo(selectedVideo, loadedDuration);
    if (resumeTime > 0) {
      videoElement.currentTime = resumeTime;
      setCurrentVideoTime(resumeTime);
      currentVideoTimeRef.current = resumeTime;
      lastTimeUpdateRef.current = resumeTime;
    }
  };

  const handleCourseSelect = async (course, shouldAutoPlay = false) => {
    setSelectedCourse(course);
    setAutoPlayVideo(shouldAutoPlay);
    setSelectedVideo(null);
    setCourseVideos([]);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/training/view-course"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ course_id: course.id }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch course videos");
      }

      const result = await response.json();
      const videos = result?.data?.videos || [];
      setCourseVideos(videos);

      const firstUnwatched = videos.find(v => !v.video_completed);
      setSelectedVideo(firstUnwatched || videos[0] || null);
    } catch {
      setCourseVideos([]);
      setSelectedVideo(null);
    }
  };

  const handleBackToAllCourses = () => {
    setSelectedCourse(null);
    setSelectedVideo(null);
    setCourseVideos([]);
    setAutoPlayVideo(false);

    if (courseVideoRef.current) {
      courseVideoRef.current.pause();
    }
  };

  const formatDuration = seconds => {
    const numericSeconds = Number(seconds);
    if (!Number.isFinite(numericSeconds) || numericSeconds < 0) return "0:00";
    const safeSeconds = Math.floor(numericSeconds);
    const minutes = Math.floor(safeSeconds / 60);
    const secs = safeSeconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const formatShortDate = dateValue => {
    if (!dateValue) return null;
    return new Date(dateValue).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const featuredCourse = courses.length > 0 ? courses[0] : null;

  const featuredProgress = featuredCourse
    ? Math.round(
        (featuredCourse.completed_video_count /
          featuredCourse.total_video_count) *
          100
      )
    : 0;

  const toDoProgramItems = programs
    .filter(program => {
      const completed = Number(program.completed_tasks || 0);
      const total = Number(program.total_tasks || 0);
      return total === 0 || completed < total;
    })
    .map(program => {
      const completed = Number(program.completed_tasks || 0);
      const total = Number(program.total_tasks || 0);
      const remainingTasks = Math.max(total - completed, 0);

      return {
        id: `program-${program.id}`,
        itemType: "program",
        entityId: program.id,
        title: program.program_name,
        thumbnailUrl: program.cover_image || null,
        duration:
          total > 0
            ? `${remainingTasks} task${remainingTasks !== 1 ? "s" : ""} left`
            : "Program",
        presenter: "Program",
        progress: total > 0 ? Math.round((completed / total) * 100) : null,
        date: formatShortDate(program.created_at),
      };
    });

  const toDoCourseItems = courses.map(course => {
    const totalVideos = Number(course.total_video_count || 0);
    const completedVideos = Number(course.completed_video_count || 0);
    const remainingVideos = Math.max(totalVideos - completedVideos, 0);

    return {
      id: `course-${course.id}`,
      itemType: "course",
      entityId: course.id,
      title: course.course_title,
      thumbnailUrl: course.course_thumbnail || null,
      duration:
        course.total_video_count != null
          ? `${remainingVideos} video${remainingVideos !== 1 ? "s" : ""} left`
          : null,
      presenter: "Course",
      progress:
        course.total_video_count > 0
          ? Math.round(
              (course.completed_video_count / course.total_video_count) * 100
            )
          : null,
      date: formatShortDate(course.created_at),
    };
  });

  const toDoBalanced = (() => {
    const max = 7;
    const half = Math.ceil(max / 2);

    let pCount = Math.min(toDoProgramItems.length, half);
    let cCount = Math.min(toDoCourseItems.length, max - pCount);

    if (pCount < half) cCount = Math.min(toDoCourseItems.length, max - pCount);
    if (cCount < half) pCount = Math.min(toDoProgramItems.length, max - cCount);

    const list = [];
    for (let i = 0; i < Math.max(pCount, cCount); i++) {
      if (i < pCount) list.push(toDoProgramItems[i]);
      if (i < cCount) list.push(toDoCourseItems[i]);
    }

    return list.slice(0, max);
  })();

  const newReleaseItems = useMemo(
    () =>
      allVideos
        .slice()
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        .map(v => ({
          id: `video-${v.id}`,
          itemType: "video",
          entityId: v.id,
          courseId: v.course_id,
          title: v.video_title,
          thumbnailUrl: v.video_thumbnail || v.course_thumbnail || null,
          pitchReviewRequired: Boolean(
            v?.require_pitch || v?.pitch_review_message
          ),
          duration: v.duration ? formatDuration(v.duration) : null,
          presenter: v.course_title || null,
          date: formatShortDate(v.created_at),
        })),
    [allVideos]
  );

  const courseOutlineItems = courseVideos.map((video, index) => ({
    id: `course-outline-${video.id}`,
    itemType: "course-video",
    entityId: video.id,
    title: `${index + 1}. ${video.video_title}`,
    thumbnailUrl:
      video.video_thumbnail || selectedCourse?.course_thumbnail || null,
    pitchReviewRequired: Boolean(
      video?.require_pitch || video?.pitch_review_message
    ),
    duration: video.duration ? formatDuration(video.duration) : null,
    completed: !!video.video_completed,
    active: video.id === selectedVideo?.id,
    presenter: video.id === selectedVideo?.id ? "Now Playing" : undefined,
  }));

  const openVideoRecommendation = async item => {
    const course = courses.find(c => c.id === item.courseId);
    if (!course) return;

    setSelectedCourse(course);
    setAutoPlayVideo(false);
    setCourseVideos([]);
    setSelectedVideo(null);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/training/view-course"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ course_id: course.id }),
      });

      if (!response.ok) return;

      const result = await response.json();
      const videos = result.data?.videos || [];
      setCourseVideos(videos);

      const targetVideo = videos.find(v => v.id === item.entityId);
      setSelectedVideo(targetVideo || videos[0] || null);
    } catch {
      setCourseVideos([]);
      setSelectedVideo(null);
    }
  };

  if (loading) {
    return <div className="training-page-no-courses-message">Loading…</div>;
  }

  if (error) {
    return (
      <DisplayMessage
        type="error"
        message={`Error loading training: ${error}`}
      />
    );
  }

  if (!featuredCourse) {
    return (
      <div className="training-page-no-courses-message">
        No training courses available at this time.
      </div>
    );
  }

  return (
    <div className="training-page-layout">
      <div className="training-page-main-content">
        <div className="training-page-hero-row">
          <div
            className="training-page-hero-section"
            style={{ display: "flex", flexDirection: "column", gap: 0 }}
          >
            <div
              className="training-page-hero-background"
              onClick={() => {
                if (selectedCourse && courseVideoRef.current) {
                  if (isVideoPlaying) {
                    courseVideoRef.current.pause();
                  } else {
                    courseVideoRef.current.play();
                  }
                }
              }}
              onMouseMove={() => {
                if (selectedCourse) {
                  setShowControls(true);
                  clearTimeout(controlsTimeoutRef.current);
                  controlsTimeoutRef.current = setTimeout(
                    () => setShowControls(false),
                    3000
                  );
                }
              }}
              onMouseLeave={() => {
                if (selectedCourse) setShowControls(false);
              }}
            >
              {selectedCourse && selectedVideo ? (
                <>
                  <div
                    className="training-page-hero-fallback-image"
                    style={{
                      backgroundImage: `url(${selectedVideo.video_thumbnail || selectedCourse.course_thumbnail})`,
                      opacity: videoReady ? 0 : 1,
                      transition: "opacity 0.3s ease",
                      zIndex: 0,
                    }}
                  />
                  <video
                    key={selectedVideo.id}
                    ref={courseVideoRef}
                    className="training-page-hero-video"
                    src={selectedVideo.video_url}
                    autoPlay={autoPlayVideo}
                    playsInline
                    style={{
                      opacity: videoReady ? 1 : 0,
                      transition: "opacity 0.3s ease",
                      zIndex: 1,
                    }}
                    onLoadedMetadata={handleCourseVideoLoadedMetadata}
                    onCanPlay={() => setVideoReady(true)}
                    onPlay={() => setIsVideoPlaying(true)}
                    onPause={() => {
                      setIsVideoPlaying(false);
                      flushVideoLogUpdate();
                      setShowControls(true);
                    }}
                    onEnded={() => {
                      setIsVideoPlaying(false);
                      flushVideoLogUpdate();
                    }}
                    onTimeUpdate={e =>
                      handleCourseVideoTimeUpdate(e.target.currentTime)
                    }
                  />

                  <div
                    className="training-page-controls-overlay"
                    style={{
                      opacity: showControls ? 1 : 0,
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    <div
                      className="training-page-controls-progress-track"
                      onClick={e => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const pct = (e.clientX - rect.left) / rect.width;
                        if (courseVideoRef.current && duration) {
                          courseVideoRef.current.currentTime = pct * duration;
                        }
                      }}
                    >
                      <div
                        className="training-page-controls-progress-fill"
                        style={{
                          width: `${duration ? (currentVideoTime / duration) * 100 : 0}%`,
                        }}
                      />
                      <div
                        className="training-page-controls-progress-dot"
                        style={{
                          left: `${duration ? (currentVideoTime / duration) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="training-page-controls-time">
                      {formatDuration(Math.floor(currentVideoTime))} /{" "}
                      {formatDuration(Math.floor(duration))}
                    </span>

                    <div className="training-page-controls-row">
                      <div className="training-page-controls-left">
                        <button
                          className="training-page-ctrl-btn"
                          onClick={() => {
                            if (courseVideoRef.current) {
                              isVideoPlaying
                                ? courseVideoRef.current.pause()
                                : courseVideoRef.current.play();
                            }
                          }}
                        >
                          {isVideoPlaying ? (
                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <rect x="6" y="4" width="4" height="16" rx="1" />
                              <rect x="14" y="4" width="4" height="16" rx="1" />
                            </svg>
                          ) : (
                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <polygon points="5,3 19,12 5,21" />
                            </svg>
                          )}
                        </button>

                        <button
                          className="training-page-ctrl-btn"
                          onClick={() => {
                            if (courseVideoRef.current)
                              courseVideoRef.current.currentTime -= 10;
                          }}
                          title="Back 10s"
                        >
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="white"
                          >
                            <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
                          </svg>
                        </button>

                        <button
                          className="training-page-ctrl-btn"
                          onClick={() => {
                            if (courseVideoRef.current)
                              courseVideoRef.current.currentTime += 10;
                          }}
                          title="Forward 10s"
                        >
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="white"
                          >
                            <path d="M18 13c0 3.31-2.69 6-6 6s-6-2.69-6-6 2.69-6 6-6v4l5-5-5-5v4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8h-2z" />
                          </svg>
                        </button>

                        <button
                          className="training-page-ctrl-btn"
                          onClick={() => {
                            if (courseVideoRef.current) {
                              const next = !isMuted;
                              setIsMuted(next);
                              courseVideoRef.current.muted = next;
                            }
                          }}
                        >
                          {isMuted ? (
                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                            </svg>
                          ) : (
                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
                            </svg>
                          )}
                        </button>
                      </div>

                      <div className="training-page-controls-center">
                        <span className="training-page-controls-course-name">
                          {selectedCourse.course_title}
                        </span>
                        <span className="training-page-controls-video-name">
                          {selectedVideo.video_title}
                        </span>
                        <span className="training-page-controls-video-count">
                          Video{" "}
                          {courseVideos.findIndex(
                            v => v.id === selectedVideo.id
                          ) + 1}{" "}
                          of {courseVideos.length}
                        </span>
                      </div>

                      <div className="training-page-controls-right">
                        {(() => {
                          const idx = courseVideos.findIndex(
                            v => v.id === selectedVideo.id
                          );
                          const hasNext = idx < courseVideos.length - 1;
                          return (
                            <button
                              className="training-page-ctrl-btn"
                              disabled={!hasNext}
                              onClick={() => {
                                if (hasNext)
                                  setSelectedVideo(courseVideos[idx + 1]);
                              }}
                              title="Next video"
                            >
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="white"
                              >
                                <path d="M6 18l8.5-6L6 6v12zm2-8.14L11.03 12 8 14.14V9.86zM16 6h2v12h-2z" />
                              </svg>
                            </button>
                          );
                        })()}

                        <button
                          className="training-page-ctrl-btn"
                          onClick={() => {
                            if (courseVideoRef.current) {
                              if (document.fullscreenElement) {
                                document.exitFullscreen();
                              } else {
                                courseVideoRef.current
                                  .closest(".training-page-hero-background")
                                  ?.requestFullscreen();
                              }
                            }
                          }}
                          title="Fullscreen"
                        >
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="white"
                          >
                            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="training-page-hero-fallback-image"
                    style={{
                      backgroundImage: `url(${featuredCourse.course_thumbnail})`,
                      opacity: showVideo && featuredVideoUrl ? 0 : 1,
                    }}
                    onMouseEnter={() => setShouldLoadVideo(true)}
                  />
                  {featuredVideoUrl && (
                    <video
                      ref={videoRef}
                      className="training-page-hero-video"
                      src={featuredVideoUrl}
                      muted
                      loop
                      playsInline
                      style={{
                        opacity: showVideo ? 1 : 0,
                        transition: "opacity 1s ease-in-out",
                      }}
                    />
                  )}
                </>
              )}

              {!selectedCourse && (
                <div className="training-page-hero-gradient-overlay" />
              )}

              {selectedCourse ? null : (
                <div className="training-page-hero-content">
                  <div className="training-page-hero-badge">
                    🎯 PICK UP WHERE YOU LEFT OFF
                  </div>
                  <h1 className="training-page-hero-title">
                    {featuredCourse.course_title}
                  </h1>
                  <div className="training-page-hero-stats">
                    <span className="training-page-hero-progress-text">
                      {featuredCourse.completed_video_count}/
                      {featuredCourse.total_video_count} videos completed
                    </span>
                    <span className="training-page-hero-progress-percentage">
                      {featuredProgress}% complete
                    </span>
                  </div>
                  <div className="training-page-hero-progress-bar-container">
                    <div className="training-page-hero-progress-bar-bg">
                      <div
                        className="training-page-hero-progress-bar-fill"
                        style={{ width: `${featuredProgress}%` }}
                      />
                    </div>
                  </div>
                  <div className="training-page-hero-buttons">
                    <button
                      className="training-page-hero-button training-page-hero-button-play"
                      onClick={() => handleCourseSelect(featuredCourse, false)}
                    >
                      ▶ Continue Learning
                    </button>
                  </div>
                </div>
              )}
            </div>

            {selectedCourse && (
              <div
                className="training-page-video-info-panel"
                style={{ marginTop: "1.5rem" }}
              >
                <div className="training-page-video-info-header">
                  {selectedVideo && (
                    <h2 className="training-page-video-info-title">
                      {selectedVideo.video_title}
                    </h2>
                  )}
                  <span className="training-page-inline-course-progress">
                    <span className="training-page-inline-course-progress-count">
                      {courseVideos.filter(v => v.video_completed).length}
                    </span>
                    /{courseVideos.length} Videos Complete
                  </span>
                </div>

                <div className="training-page-video-info-tabs">
                  <button
                    className={`training-page-video-info-tab ${
                      activeInfoTab === "description" ? "active" : ""
                    }`}
                    onClick={() => setActiveInfoTab("description")}
                  >
                    Description
                  </button>
                  <button
                    className={`training-page-video-info-tab ${
                      activeInfoTab === "transcription" ? "active" : ""
                    }`}
                    onClick={() => setActiveInfoTab("transcription")}
                  >
                    Transcription
                  </button>
                  <button
                    className={`training-page-video-info-tab ${
                      activeInfoTab === "resources" ? "active" : ""
                    }`}
                    onClick={() => setActiveInfoTab("resources")}
                  >
                    Resource Viewer
                  </button>
                </div>

                {activeInfoTab === "transcription" ? (
                  <div className="training-page-video-transcription">
                    {selectedVideo?.transcription ||
                    selectedVideo?.video_transcript ? (
                      <p>
                        {selectedVideo.transcription ||
                          selectedVideo.video_transcript}
                      </p>
                    ) : (
                      <p className="training-page-video-transcription-empty">
                        No transcription available for this video.
                      </p>
                    )}
                  </div>
                ) : activeInfoTab === "resources" ? (
                  <div className="training-page-resource-viewer">
                    <p className="training-page-resource-viewer-intro">
                      Course resources and supplementary materials for{" "}
                      <strong>{selectedCourse?.course_title}</strong>.
                    </p>
                    {courseVideos.length > 0 ? (
                      <div className="training-page-resource-viewer-list">
                        {courseVideos.map((v, idx) => {
                          const isActive = v.id === selectedVideo?.id;
                          const isWatched = v.video_completed;
                          const thumbUrl =
                            v.video_thumbnail ||
                            selectedCourse?.course_thumbnail ||
                            null;

                          return (
                            <div
                              key={v.id}
                              className={`training-page-resource-item ${
                                isActive ? "active" : ""
                              }`}
                              onClick={() => setSelectedVideo(v)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setSelectedVideo(v);
                                }
                              }}
                            >
                              <div className="training-page-resource-item-thumb">
                                {thumbUrl ? (
                                  <img src={thumbUrl} alt={v.video_title} />
                                ) : (
                                  <div className="training-page-resource-item-thumb-placeholder">
                                    ▶
                                  </div>
                                )}
                                {isWatched && (
                                  <div className="training-page-resource-item-watched">
                                    ✓
                                  </div>
                                )}
                              </div>
                              <div className="training-page-resource-item-info">
                                <span className="training-page-resource-item-number">
                                  {idx + 1}
                                </span>
                                <div className="training-page-resource-item-details">
                                  <span className="training-page-resource-item-title">
                                    {v.video_title}
                                  </span>
                                  {v.video_desc && (
                                    <span className="training-page-resource-item-desc">
                                      {v.video_desc}
                                    </span>
                                  )}
                                </div>
                                {isActive && (
                                  <span className="training-page-resource-item-playing">
                                    Now Playing
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="training-page-video-transcription-empty">
                        No resources available for this course.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="training-page-video-description">
                    {selectedVideo?.video_desc ||
                      selectedVideo?.video_description ||
                      "No description available for this video."}
                  </div>
                )}

                <div style={{ marginTop: "1rem" }}>
                  <button
                    type="button"
                    className="training-page-video-info-tab"
                    onClick={handleBackToAllCourses}
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            )}

            {!selectedCourse &&
              (() => {
                const todoPgms = programs.filter(p => {
                  const completed = Number(p.completed_tasks || 0);
                  const total = Number(p.total_tasks || 0);
                  return total === 0 || completed < total;
                });
                const pendingPgms = programs.filter(p => {
                  const completed = Number(p.completed_tasks || 0);
                  const total = Number(p.total_tasks || 0);
                  return (
                    total > 0 && completed >= total && p.status !== "completed"
                  );
                });
                const completedPgms = programs.filter(
                  p => p.status === "completed"
                );

                const filteredPgms =
                  programsFilterTab === "To Do"
                    ? todoPgms
                    : programsFilterTab === "Pending"
                      ? pendingPgms
                      : completedPgms;

                const visiblePgms = filteredPgms.slice(0, 4);

                return (
                  <div
                    className="training-page-programs-section"
                    style={{ marginTop: "1.5rem" }}
                  >
                    <div className="training-page-programs-header">
                      <h2 className="training-page-programs-title">Programs</h2>
                      <button
                        className="training-page-programs-view-all"
                        onClick={() => navigate("/programs")}
                      >
                        View all
                      </button>
                    </div>

                    <div
                      className="training-page-video-info-tabs"
                      style={{ marginTop: 0, marginBottom: "0.9rem" }}
                    >
                      {["To Do", "Pending", "Completed"].map(tab => (
                        <button
                          key={tab}
                          className={`training-page-video-info-tab ${
                            programsFilterTab === tab ? "active" : ""
                          }`}
                          onClick={() => setProgramsFilterTab(tab)}
                        >
                          {tab}
                          {tab === "To Do" && todoPgms.length > 0 && (
                            <span className="training-page-programs-pill-count">
                              {todoPgms.length}
                            </span>
                          )}
                          {tab === "Pending" && pendingPgms.length > 0 && (
                            <span className="training-page-programs-pill-count">
                              {pendingPgms.length}
                            </span>
                          )}
                          {tab === "Completed" && completedPgms.length > 0 && (
                            <span className="training-page-programs-pill-count">
                              {completedPgms.length}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    <div
                      className={`training-page-programs-content ${
                        programsFilterTab === "To Do"
                          ? "training-page-programs-content-todo"
                          : ""
                      }`}
                    >
                      {visiblePgms.length > 0 ? (
                        <div className="training-page-programs-carousel">
                          {visiblePgms.map((program, idx) => (
                            <div
                              key={program.id}
                              className="training-page-program-card"
                              onClick={() =>
                                navigate(`/programs/${program.id}`, {
                                  state: {
                                    program,
                                    fromTraining: true,
                                    trainingTab: "Home",
                                  },
                                })
                              }
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  navigate(`/programs/${program.id}`, {
                                    state: {
                                      program,
                                      fromTraining: true,
                                      trainingTab: "Home",
                                    },
                                  });
                                }
                              }}
                            >
                              <div className="training-page-program-card-thumb">
                                {program.cover_image ? (
                                  <img
                                    src={program.cover_image}
                                    alt={program.program_name}
                                    className="training-page-program-card-image"
                                  />
                                ) : (
                                  <div
                                    className={`training-page-program-card-placeholder training-page-program-card-placeholder-${idx % 4}`}
                                  >
                                    <IconSchool
                                      size={34}
                                      color="rgba(255,255,255,0.9)"
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="training-page-program-card-content">
                                <h3 className="training-page-program-card-title">
                                  {program.program_name}
                                </h3>
                                <p className="training-page-program-card-meta">
                                  {programsFilterTab === "Pending"
                                    ? "Awaiting approval"
                                    : programsFilterTab === "Completed"
                                      ? "Completed"
                                      : `${program.completed_tasks || 0} of ${program.total_tasks || 0} tasks completed`}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="training-page-programs-empty">
                          No {programsFilterTab.toLowerCase()} programs.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
          </div>

          <div
            className="training-page-hero-pitch-panel"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <VideoRecommendationsPanel
              tabs={
                selectedCourse
                  ? ["Course Outline", "New Release"]
                  : ["To Do", "New Release"]
              }
              itemsByTab={
                selectedCourse
                  ? {
                      "Course Outline": courseOutlineItems,
                      "New Release": newReleaseItems,
                    }
                  : {
                      "To Do": toDoBalanced,
                      "New Release": newReleaseItems,
                    }
              }
              onItemClick={async item => {
                if (item.itemType === "course-video") {
                  const courseVideo = courseVideos.find(
                    v => v.id === item.entityId
                  );
                  if (courseVideo) {
                    setSelectedVideo(courseVideo);
                  }
                  return;
                }

                if (item.itemType === "program") {
                  navigate(`/programs/${item.entityId}`, {
                    state: {
                      fromTraining: true,
                      trainingTab: "Home",
                    },
                  });
                  return;
                }

                if (item.itemType === "course") {
                  const course = courses.find(c => c.id === item.entityId);
                  if (course) handleCourseSelect(course, false);
                  return;
                }

                if (item.itemType === "video") {
                  await openVideoRecommendation(item);
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrainingTabHomePage;
